import { useMemo, useCallback, useState, useRef } from 'react';
import { css } from '@emotion/react';
import {
  DeviceInclude_Enum,
  DeviceType_Enum,
  DeviceOwner_Enum,
  DeviceMake_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import {
  RenameLineResponse,
  RenameLineChangeSet,
} from '@weave/schema-gen-ts/dist/schemas/phone-devices/lines/line_api.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { capitalize } from 'lodash-es';
import { useQueryClient } from 'react-query';
import { DevicesApi, DevicesQueries, DevicesQueryKey, type DevicesTypes } from '@frontend/api-devices';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Text, TextLink, useAlert, useModalControl, Chip, useTooltip } from '@frontend/design-system';
import { EditableField } from '../../../components/common/editable-cell';
import { IndexTable } from '../../../components/common/index-table';
import { CallGroupSideModal } from '../../../components/devices/device-settings/call-group-side-modal';
import { WeaveSupportLink } from '../../../components/weave-support-link';
import { trackingId } from '../../../tracking';
import { logError } from '../../../utils/loggers';
import { validateDeviceName, validateExtension } from '../../../utils/validators';
import { usePersistentPagination } from '../usePersistentPagination';
import { getChangedFields, buildRenameLineSuccessMessage, updateDeviceCaches } from './rename-line-helpers';

type DeviceInfoType = {
  deviceId: string;
  deviceName: string;
  callGroups: DevicesTypes.ListDeviceSipProfileTypes['callGroups'];
};

export const DeviceSettingsTable = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasDeviceWritePermission =
    hasSchemaACL(selectedLocationIds[0], Permission.CREATE_DEVICES) ||
    hasSchemaACL(selectedLocationIds[0], Permission.PHONE_FULL_ACCESS) ||
    isWeaveUser();
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const { navigate } = useSettingsNavigate();
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const queryClient = useQueryClient();
  const alert = useAlert();
  const sideModal = useModalControl();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfoType>();
  const [editingCellId, setEditingCellId] = useState<string | null>(null);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  // We need to freeze the sort order of the table when an inline edit is made to prevent the row from jumping around
  // due to re-sorting after the cache update. This state tracks whether the sort is currently frozen.
  const [isSortFrozen, setIsSortFrozen] = useState(false);
  // Captures a Map<deviceId, position> of the row order at freeze time
  const frozenOrderRef = useRef<Map<string, number>>(new Map());
  const tableInstanceRef = useRef<any>(null);
  const [_pagination, _setPagination, paginationTableProps, paginationConfig] =
    usePersistentPagination('device-settings-pagination');
  const locations =
    settingsTenantLocation?.children && settingsTenantLocation.children.length > 0
      ? settingsTenantLocation?.children.filter((location) => filteredLocations.includes(location.id))
      : [settingsTenantLocation].filter(Boolean);

  const locationIds = locations.map((loc: { id: string }) => loc.id);

  const { data: { devices } = { devices: [] }, isLoading: isLoadingDevices } = DevicesQueries.useGetDevicesList({
    include: [DeviceInclude_Enum.CALL_GROUPS, DeviceInclude_Enum.E911_ADDRESS, DeviceInclude_Enum.REGISTRATION],
    locationIds,
    tenantId: settingsTenantLocation?.phoneTenantId,
  });

  const prefilteredDevices = useMemo(
    () =>
      scopedFilterAndSort({
        data: devices,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
        scopeIdAccessor: (device) => (device.location?.locationId ? [device.location?.locationId] : []),
      }),
    [devices, globalAvailableLocationIds, filteredLocations]
  );

  const filteredDevices = useMemo(() => {
    const filtered = prefilteredDevices.filter(
      (device) => device.type === DeviceType_Enum.DESK_PHONE || device.type === DeviceType_Enum.SOFTPHONE
    );

    return filtered ?? [];
  }, [prefilteredDevices, filteredLocations, settingsTenantLocation?.locationType]);

  const { data: extData } = DevicesQueries.useListExtensions(
    { tenantId: settingsTenantLocation?.phoneTenantId ?? '' },
    { enabled: !!settingsTenantLocation?.phoneTenantId }
  );
  const extensionsInUse = new Set(extData?.extensions?.map((ext) => ext.number) ?? []);

  const unfreezeSort = useCallback(() => {
    setIsSortFrozen(false);
    frozenOrderRef.current = new Map();
  }, []);

  // When frozen, re-order filteredDevices to match the captured row order so the edited row stays in place.
  const stableDevices = useMemo(() => {
    if (!isSortFrozen || frozenOrderRef.current.size === 0) return filteredDevices;
    const orderMap = frozenOrderRef.current;
    return [...filteredDevices].sort((a, b) => {
      const indexA = orderMap.get(a.deviceId) ?? Number.MAX_SAFE_INTEGER;
      const indexB = orderMap.get(b.deviceId) ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }, [filteredDevices, isSortFrozen]);

  const handleSetFilteredLocations = useCallback(
    (newLocations: string[]) => {
      setFilteredLocations(newLocations);
      unfreezeSort();
    },
    [unfreezeSort]
  );

  const handleCallGroupSideModal = (info: DeviceInfoType) => {
    setDeviceInfo(info);
    sideModal.openModal();
  };

  // Highlights the changed cells briefly after a successful save.
  // Adds a CSS class to the parent <div role="cell"> so the entire table cell flashes.
  const flashCells = useCallback((deviceId: string, changes: RenameLineChangeSet | undefined) => {
    if (!changes) return;
    const cellIds: string[] = [];
    if (changes.extensionNumber && changes.extensionNumber.before !== changes.extensionNumber.after) {
      cellIds.push(`${deviceId}-extension`);
    }
    if (changes.deviceName && changes.deviceName.before !== changes.deviceName.after) {
      cellIds.push(`${deviceId}-deviceName`);
    }
    if (cellIds.length === 0) return;

    cellIds.forEach((cellId) => {
      const field = document.querySelector(`.editable-field-${cellId}`);
      const cell = field?.closest('div[role="cell"]'); // find the parent cell container
      const target = cell ?? field;
      target?.classList.add('highlighted');
      setTimeout(() => target?.classList.remove('highlighted'), 800);
    });
  }, []);

  const handleSaveSuccess = useCallback(
    (deviceId: string, response: RenameLineResponse) => {
      // Capture the current row order before the cache update triggers a re-render/re-sort
      if (tableInstanceRef.current?.rows) {
        // Map<deviceId, position>
        const orderMap = new Map<string, number>();
        tableInstanceRef.current.rows.forEach((row: any, index: number) => {
          if (row.original?.deviceId) {
            orderMap.set(row.original.deviceId, index);
          }
        });
        frozenOrderRef.current = orderMap;
        setIsSortFrozen(true);
      }

      updateDeviceCaches({
        queryClient,
        changes: response.changes,
        deviceId,
        tenantId: settingsTenantLocation?.phoneTenantId,
        sipProfileId: response.line?.sipProfile.id,
      });

      const changedFields = getChangedFields(response.changes);
      const message = buildRenameLineSuccessMessage(changedFields);
      // invalidate extensions list to update the "in use" status of extensions after a change to an extension number
      queryClient.invalidateQueries([
        settingsTenantLocation?.phoneTenantId,
        ...DevicesQueryKey.queryKeys.listExtensions(),
      ]);
      alert.success(message);

      flashCells(deviceId, response.changes);
    },
    [queryClient, settingsTenantLocation?.phoneTenantId, t, alert, flashCells]
  );

  const { mutate: mutateDevice, isLoading: isMutatingDevice } = useMutation({
    mutationFn: DevicesApi.RenameLine,
  });

  return (
    <>
      {settingsTenantLocation ? (
        <>
          <IndexTable
            filteredLocations={filteredLocations}
            setFilteredLocations={handleSetFilteredLocations}
            tenantLocation={settingsTenantLocation}
            idAccessor={(data) => data.deviceId}
            nameAccessor={(data) => data.name}
            labelsAccessor={(data) => (data.location?.locationId ? [data.location?.locationId] : [])}
            labelsColumnPosition={4}
            rowActions={[
              {
                Icon: () => <Icon name='settings' />,
                disabled: !hasDeviceWritePermission,
                label: t('Edit Device'),
                onClick: ({ deviceId }) => {
                  if (deviceId) {
                    navigate({ to: '/phone/devices/:id', params: { id: deviceId }, search: { id: deviceId } });
                  }
                },
              },
            ]}
            {...paginationTableProps}
            setRowsPerPage={(size: number) => {
              paginationTableProps.setRowsPerPage(size);
              unfreezeSort();
            }}
            tableConfig={{
              data: stableDevices,
              instanceRef: tableInstanceRef,
              isLoading: isLoadingDevices,
              manualSortBy: isSortFrozen,
              onSortChange: isSortFrozen ? unfreezeSort : undefined,
              hasGlobalSearch: true,
              globalSearchConfig: { onSearchChange: unfreezeSort },
              tableStyle: cellHighlightStyle,
              ...paginationConfig,
              colConfig: [
                {
                  Header: t('Device Name'),
                  accessor: (device) => device.name,
                  id: 'deviceName',
                  minWidth: 280,
                  cellRenderer: (cellValue, rowData) => {
                    const name = (cellValue as string) ?? '';
                    if (!rowData) {
                      return <Text>{name}</Text>;
                    }

                    const cellId = `${rowData.deviceId}-deviceName`;
                    return (
                      <EditableField
                        value={name}
                        displayName={t('Device Name')}
                        fieldId={cellId}
                        isEditing={editingCellId === cellId}
                        isSaving={isMutatingDevice}
                        onStartEdit={setEditingCellId}
                        onCancelEdit={() => setEditingCellId(null)}
                        validatorFn={validateDeviceName}
                        useTooltipError
                        trackingIdPrefix='pc-device-settings-table-device-name-'
                        showCancelButton={false}
                        onSave={async (newValue) => {
                          mutateDevice(
                            {
                              tenantId: settingsTenantLocation.phoneTenantId,
                              deviceId: rowData.deviceId,
                              extensionId: rowData.sipProfiles?.[0]?.extension?.extensionId ?? '',
                              deviceName: newValue,
                            },
                            {
                              onSuccess: (response) => {
                                handleSaveSuccess(rowData.deviceId, response);
                              },
                              onError: (error) => {
                                logError('DeviceSettingsTable:mutateDevice | Failed to update Device Name', {
                                  data: {
                                    tenantId: settingsTenantLocation.phoneTenantId,
                                    deviceId: rowData.deviceId,
                                    extensionId: rowData.sipProfiles?.[0]?.extension?.extensionId ?? '',
                                    deviceName: newValue,
                                    hasDeviceWritePermission,
                                    error,
                                  },
                                });
                                alert.error(t('Failed to update Device Name'));
                              },
                            }
                          );
                        }}
                      />
                    );
                  },
                },
                {
                  Header: t('Extension'),
                  id: 'extension',
                  startingSortBy: 'asc',
                  accessor: ({ sipProfiles }) => sipProfiles?.[0]?.extension?.extensionNumber,
                  minWidth: 150,
                  maxWidth: 150,
                  cellRenderer: (cellValue, rowData) => {
                    const extensionStr = cellValue?.toString() ?? '';
                    if (!rowData) {
                      return <Text>{extensionStr}</Text>;
                    }

                    const invalidExtensions = new Set(extensionsInUse);
                    const currentExtension = rowData.sipProfiles?.[0]?.extension?.extensionNumber;
                    if (currentExtension) {
                      invalidExtensions.delete(currentExtension);
                    }

                    const cellId = `${rowData.deviceId}-extension`;
                    return (
                      <EditableField
                        value={extensionStr}
                        displayName={t('Extension')}
                        fieldConfig={{ type: 'number', includeThousandsSeparator: false, allowDecimal: false }}
                        fieldId={cellId}
                        isEditing={editingCellId === cellId}
                        isSaving={isMutatingDevice}
                        onStartEdit={setEditingCellId}
                        onCancelEdit={() => setEditingCellId(null)}
                        validatorFn={(value) => validateExtension(value, invalidExtensions)}
                        useTooltipError
                        trackingIdPrefix='pc-device-settings-table-extension-'
                        showCancelButton={false}
                        onSave={(newValue) => {
                          mutateDevice(
                            {
                              tenantId: settingsTenantLocation.phoneTenantId,
                              deviceId: rowData.deviceId,
                              extensionId: rowData.sipProfiles?.[0]?.extension?.extensionId ?? '',
                              extensionNumber: newValue,
                            },
                            {
                              onSuccess: (response) => {
                                handleSaveSuccess(rowData.deviceId, response);
                              },
                              onError: (error) => {
                                logError('DeviceSettingsTable:mutateDevice | Failed to update Extension', {
                                  data: {
                                    tenantId: settingsTenantLocation.phoneTenantId,
                                    deviceId: rowData.deviceId,
                                    extensionId: rowData.sipProfiles?.[0]?.extension?.extensionId ?? '',
                                    extensionNumber: newValue,
                                    hasDeviceWritePermission,
                                    error,
                                  },
                                });
                                alert.error(t('Failed to update Extension'));
                              },
                            }
                          );
                        }}
                      />
                    );
                  },
                },
                {
                  Header: t('Device Route'),
                  id: 'deviceRoute',
                  accessor: (device) => device,
                  minWidth: 150,
                  maxWidth: 150,
                  cellRenderer: ({ callRoute }) => {
                    const deviceRouteId = callRoute?.id;

                    return (
                      <>
                        {deviceRouteId ? (
                          <TextLink
                            onClick={() => {
                              navigate({ to: '/phone/call-routes/:id', params: { id: deviceRouteId } });
                            }}
                            trackingId={trackingId({
                              context: 'setting',
                              feature: 'devices',
                              details: 'manage-route-link',
                            })}
                          >
                            {t('Manage Route')}
                          </TextLink>
                        ) : (
                          '--'
                        )}
                      </>
                    );
                  },
                },
                {
                  Header: t('Make & Model'),
                  id: 'make-model',
                  accessor: (device) => device,
                  cellRenderer: ({ make, model, owner, type }) => {
                    const showMakeModel = make !== DeviceMake_Enum.UNSPECIFIED;
                    return (
                      <>
                        {isWeaveUser() && owner !== DeviceOwner_Enum.UNSPECIFIED && showMakeModel && (
                          <DeviceOwnerIndicatorChip owner={owner} />
                        )}
                        {type === DeviceType_Enum.DESK_PHONE && (
                          <Text>
                            {capitalize(make?.toLocaleLowerCase())} {model?.toLocaleUpperCase()}
                          </Text>
                        )}
                        {type === DeviceType_Enum.SOFTPHONE && <Text>{t('Softphone')}</Text>}
                      </>
                    );
                  },
                },
                {
                  Header: t('Unique ID'),
                  accessor: ({ macAddress, userEmail }) => ({ macAddress, userEmail }),
                  cellRenderer: ({ macAddress, userEmail }) => {
                    const identifier = macAddress ? 'MAC:' : 'User:';
                    const id = macAddress ? macAddress : userEmail ?? '';
                    return (
                      <>
                        <Text weight='bold'>{identifier}</Text>&nbsp;
                        <Text>{id}</Text>
                      </>
                    );
                  },
                  id: 'uniqueId',
                  minWidth: 220,
                  disableSortBy: true,
                },
                {
                  Header: t('E911 Address'),
                  accessor: ({ e911Address }) => e911Address?.name,
                  id: 'e911Address',
                  disableSortBy: true,
                },
                {
                  Header: t('Call Group Assignments'),
                  accessor: (device) => device,
                  cellRenderer: ({
                    name,
                    deviceId,
                    sipProfiles,
                  }: {
                    name: string;
                    deviceId: string;
                    sipProfiles: DevicesTypes.ListDeviceSipProfileTypes[];
                  }) => {
                    if (!sipProfiles.length || !sipProfiles?.[0]?.callGroups?.length) return '-';
                    const callGroups = sipProfiles?.[0]?.callGroups ?? [];
                    return (
                      <TextLink onClick={() => handleCallGroupSideModal({ deviceName: name, deviceId, callGroups })}>
                        {t('{{count}} Call Groups', { count: callGroups?.length })}
                      </TextLink>
                    );
                  },
                  id: 'callGroupAssignments',
                  disableSortBy: true,
                },
              ],
              emptyStateConfig: {
                type: 'sync_your_phone',
                header: t('No Devices'),
                description: () => (
                  <Trans t={t}>
                    <Text textAlign='center'>You don't have any devices available.</Text>
                    <Text textAlign='center'>
                      Please contact <WeaveSupportLink /> to add one.
                    </Text>
                  </Trans>
                ),
              },
            }}
          />
          {sideModal.modalProps.show && (
            <CallGroupSideModal
              locationIds={locationIds}
              deviceId={deviceInfo?.deviceId}
              deviceName={deviceInfo?.deviceName}
              callGroups={deviceInfo?.callGroups}
              modalControls={sideModal}
            />
          )}
        </>
      ) : (
        <div>{t('No Phone Tenant Selected')}</div>
      )}
    </>
  );
};
const cellHighlightStyle = css`
  div[role='cell'] {
    transition: background 0.8s ease-in-out;
  }
  div[role='cell'].highlighted {
    background: ${theme.colors.indigo10};
    transition: none;
  }
`;

export const DeviceOwnerIndicatorChip = ({ owner }: { owner?: DeviceOwner_Enum }) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });

  return (
    <>
      <Chip variant='neutral' {...triggerProps} css={{ marginRight: theme.spacing(0.5) }}>
        {owner === DeviceOwner_Enum.WEAVE && <Icon color='default' name='weave-logo-small' />}
        {owner === DeviceOwner_Enum.CUSTOMER && <Icon color='default' name='building-small' />}
      </Chip>
      <Tooltip {...tooltipProps}>
        {owner === DeviceOwner_Enum.WEAVE && (
          <Text size='medium' color='white'>
            {t('Purchased from Weave')}
          </Text>
        )}
        {owner === DeviceOwner_Enum.CUSTOMER && (
          <Text size='medium' color='white'>
            {t('Customer-owned Device')}
          </Text>
        )}
      </Tooltip>
    </>
  );
};
